import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { listManageableClusters, listClusterAdmins, createAccount, addAdminToCluster, removeAdminFromCluster, type AccountSummary } from '../../lib/accounts-api';
import type { ManageableClustersResponse } from '@livinhome/shared';
import { extractApiError, extractApiErrorDetails } from '../../lib/api-client';
import { CredentialsDialog } from '../platform/CredentialsDialog';
import { AccountRowActions, CreateAccountDialogShell } from './AccountDialogs';
import { useT } from '../../i18n';

type Dev = ManageableClustersResponse['developers'][number];

export function ClusterAdminsPage() {
  const t = useT();
  const [devs, setDevs] = useState<Dev[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
  const [admins, setAdmins] = useState<AccountSummary[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [credits, setCredits] = useState<{ email: string; password: string } | null>(null);

  async function loadClusters() {
    setLoading(true); setError(null);
    try { setDevs(await listManageableClusters()); } catch (e) { setError(extractApiError(e).message); } finally { setLoading(false); }
  }
  useEffect(() => { loadClusters(); }, []);

  async function selectCluster(cl: { id: string; name: string }) {
    setSelected(cl); setAdminsLoading(true);
    try { setAdmins(await listClusterAdmins(cl.id)); } catch (e) { setError(extractApiError(e).message); } finally { setAdminsLoading(false); }
  }
  async function refreshAdmins() { if (selected) await selectCluster(selected); }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>{t('cadmin.title')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('cadmin.subtitle')}</Typography>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : devs.length === 0 ? (
        <Card variant="outlined"><CardContent><Typography color="text.secondary">{t('cadmin.noClusters')}</Typography></CardContent></Card>
      ) : (
        devs.map((d) => (
          <Card key={d.id} variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: d.clusters.length ? 1 : 0 }}>
                <Typography variant="h6">{d.name}</Typography>
                <Chip size="small" label={t('invite.clustersChip', { count: d.clusters.length, plural: d.clusters.length === 1 ? '' : 's' })} />
              </Stack>
              {d.clusters.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('cadmin.noClusterYet')}</Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {d.clusters.map((cl) => (
                    <Button key={cl.id} size="small" variant={selected?.id === cl.id ? 'contained' : 'outlined'} onClick={() => selectCluster({ id: cl.id, name: cl.name })}>
                      {cl.name}
                    </Button>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {selected ? (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">{t('cadmin.adminsTitle', { name: selected.name })}</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>{t('cadmin.createBtn')}</Button>
            </Stack>
            {adminsLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : admins.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('cadmin.noAdmins')}</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>{t('common.colName')}</TableCell><TableCell>{t('common.email')}</TableCell><TableCell>{t('common.status')}</TableCell><TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {admins.map((a) => (
                      <TableRow key={a.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <span>{a.full_name}</span>
                            {a.cluster_count && a.cluster_count > 1 ? <Chip size="small" label={t('cadmin.manages', { count: a.cluster_count })} /> : null}
                          </Stack>
                        </TableCell>
                        <TableCell>{a.email}</TableCell>
                        <TableCell>{a.status}</TableCell>
                        <TableCell align="right"><AccountRowActions account={a} onChanged={refreshAdmins} onRemoveFromCluster={async () => { await removeAdminFromCluster(a.id, selected.id); }} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      ) : null}

      <CreateClusterAdminDialog
        open={creating}
        clusterName={selected?.name ?? ''}
        clusterId={selected?.id ?? ''}
        onClose={() => setCreating(false)}
        onCreated={(email, password) => { setCreating(false); setCredits({ email, password }); refreshAdmins(); }}
        onAttached={() => { setCreating(false); refreshAdmins(); }}
      />
      <CredentialsDialog credits={credits} onClose={() => setCredits(null)} />
    </Stack>
  );
}

function CreateClusterAdminDialog({ open, clusterId, clusterName, onClose, onCreated, onAttached }: {
  open: boolean; clusterId: string; clusterName: string; onClose: () => void; onCreated: (email: string, password: string) => void; onAttached: () => void;
}) {
  const t = useT();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attach, setAttach] = useState<{ userId: string; fullName: string } | null>(null);
  useEffect(() => { if (open) { setFullName(''); setEmail(''); setError(null); setAttach(null); } }, [open]);
  async function save() {
    setBusy(true); setError(null);
    try {
      const res = await createAccount({ type: 'cluster_admin', target_id: clusterId, full_name: fullName.trim(), email: email.trim() });
      onCreated(res.user.email, res.temp_password);
    } catch (e) {
      const details = extractApiErrorDetails(e);
      if (details?.reason === 'attachable') {
        setAttach({ userId: details.user_id as string, fullName: details.full_name as string });
      } else {
        setError(extractApiError(e).message);
      }
    } finally { setBusy(false); }
  }
  async function doAttach() {
    if (!attach) return;
    setBusy(true); setError(null);
    try { await addAdminToCluster(attach.userId, clusterId); onAttached(); }
    catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
  }
  return (
    <CreateAccountDialogShell title={t('cadmin.dialogTitle', { cluster: clusterName })} open={open} onClose={onClose} busy={busy} canSave={Boolean(fullName.trim() && email.trim()) && !attach} onSave={save} error={error}>
      <TextField label={t('common.fullName')} value={fullName} onChange={(e) => setFullName(e.target.value)} required fullWidth disabled={Boolean(attach)} />
      <TextField label={t('common.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth disabled={Boolean(attach)} />
      {attach ? (
        <Alert
          severity="info"
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" disabled={busy} onClick={() => setAttach(null)}>{t('common.cancel')}</Button>
              <Button color="inherit" size="small" variant="outlined" disabled={busy} onClick={doAttach}>{busy ? t('cadmin.adding') : t('cadmin.add')}</Button>
            </Stack>
          }
        >
          <strong>{attach.fullName}</strong> already administers another cluster in this developer. Add them to {clusterName}?
        </Alert>
      ) : null}
    </CreateAccountDialogShell>
  );
}
